import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { TranslationsKeys } from '../../../../../locales/translations';
import {
  StudentsRemovalRequestState,
  StudentsRemovalRequestStudent,
} from '../../types/students';

export function useColumns({
  onCreateRequest,
  onViewDetail,
}: {
  onCreateRequest: (studyPlanEnrollmentId: number) => void;
  onViewDetail: (studyPlanEnrollmentId: number) => void;
}): ColumnTable<StudentsRemovalRequestStudent>[] {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_STUDENTS,
  );

  const table = useMemo(() => {
    return t<
      string,
      {
        headers: {
          studentName: string;
          studentIdentification: string;
          studyPlanVersionName: string;
          studyPlanEnrollmentStatus: string;
          requestedBy: string;
          viewDetail: string;
          action: string;
        };
        request: string;
      }
    >('students.table', {
      returnObjects: true,
    });
  }, [t]);

  const isRequestPendingOrApproved = useCallback((state?: string) => {
    return (
      state === StudentsRemovalRequestState.Pending ||
      state === StudentsRemovalRequestState.Approved
    );
  }, []);

  return useMemo(
    () => [
      {
        columnName: 'sectionCode',
        headerText: table.headers.studentName,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            {row.student.accountLastNames}, {row.student.accountNames}
          </>
        ),
      },
      {
        columnName: 'student',
        headerText: table.headers.studentIdentification,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            {row.student.identificationTypeName}{' '}
            {row.student.identificationValueFormatted}
          </>
        ),
      },
      {
        columnName: 'studyPlanVersion',
        headerText: table.headers.studyPlanVersionName,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => <>{row.studyPlanVersion.name}</>,
      },
      {
        columnName: 'studyPlanEnrollment',
        headerText: table.headers.studyPlanEnrollmentStatus,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => <>{row.studyPlanEnrollment.statusName}</>,
      },
      {
        columnName: 'removalRequest',
        headerText: table.headers.requestedBy,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.removalRequest &&
          row.removalRequest.state !== StudentsRemovalRequestState.Rejected ? (
            <>
              <div>
                {row.removalRequest.requestedBy.accountLastNames},{' '}
                {row.removalRequest.requestedBy.accountNames}
              </div>
              <div>
                {dayjs(row.removalRequest.requestedAt).format('DD/MM/YYYY')}
              </div>
            </>
          ) : null,
      },
      {
        columnName: 'detail',
        headerText: table.headers.viewDetail,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.removalRequest &&
          row.removalRequest.state !== StudentsRemovalRequestState.Rejected && (
            <>
              <span
                onClick={() => onViewDetail(row.studyPlanEnrollment.id)}
                className="cursor-pointer"
              >
                <Icon name="eye" size="25px" />
              </span>
            </>
          ),
      },
      {
        columnName: 'action',
        headerText: table.headers.action,
        tdClassName: 'text-center text-primary',
        thClassName: 'text-center',
        width: 120,
        cellFormat: ({ row }) => {
          return (
            <Button
              type="button"
              text={table.request}
              size="sm"
              color="primary"
              disabled={isRequestPendingOrApproved(row.removalRequest?.state)}
              onClick={() => onCreateRequest(row.studyPlanEnrollment.id)}
            />
          );
        },
      },
    ],
    [onCreateRequest, table, onViewDetail, isRequestPendingOrApproved],
  );
}
